import { AuthUserPermission, Can, useOrgUser } from '@cibo/profile'
import { Container, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Outlet, generatePath, useLocation, useParams } from 'react-router-dom'

import { useProgramContent } from '@cibo/programs'
import { useParamAsInteger } from '@cibo/ui'
import { SaveIndicatorProvider } from '@cibo/ui/src/components/SaveIndicator/SaveIndicatorProvider'
import { useCallback, useEffect } from 'react'
import { BreadcrumbNavigation } from '../../components/ImpactLayout'
import { ProgramEngagementProvider } from '../../features/ProgramEngagement'
import {
  GROWERS_PATHS,
  GROWERS_ROUTES,
  GrowersRouteParams,
} from '../../navigation/types/growersRoutes'
import { Forbidden } from '../Forbidden'

export const GrowersLayout = () => {
  const location = useLocation()
  const allPaths = location.pathname.split('/')
  // only breadcrumb up to the engagement name
  const paths = allPaths.slice(1, 4)
  const { t } = useTranslation('impact/pages/GrowersLayout')

  const { programId } = useParams<GrowersRouteParams>()
  const userId = useParamAsInteger('userId')

  const user = useOrgUser(userId)
  const program = useProgramContent(programId)

  useEffect(() => window.scrollTo(0, 0), [location.pathname])

  const pathLabel = useCallback(
    (path: string) => {
      if (path === userId?.toString()) {
        if (user.data?.displayNameFull) {
          return user.data.displayNameFull
        }
        return t('loading')
      }
      if (path === programId) {
        if (program.data?.content?.fields?.title) {
          return program.data.content.fields.title
        }
        return t('loading')
      }
      return t(`path_${path}`)
    },
    [user.dataUpdatedAt, userId, program.dataUpdatedAt, programId]
  )

  if (allPaths.includes(GROWERS_PATHS.ADD_FIELD)) {
    return <Outlet />
  }

  const enrollmentRoute = generatePath(GROWERS_ROUTES.ENROLLMENTS, {
    userId: !!userId ? `${userId}` : '',
  })

  return (
    <Can useAny={[AuthUserPermission.GROWERS_MODULE]} fallback={<Forbidden />}>
      <SaveIndicatorProvider>
        <ProgramEngagementProvider
          basePath={generatePath(GROWERS_ROUTES.PROGRAM, {
            userId: !!userId ? `${userId}` : '',
            programId: programId ?? '',
          })}
          programId={programId ?? ''}
          userId={userId || Number.NaN}
        >
          <Container>
            <Stack flex={1}>
              {paths.length > 1 && (
                <BreadcrumbNavigation
                  breadcrumbs={paths.map((path, index) => ({
                    name: pathLabel(path),
                    to:
                      index === paths.length - 1
                        ? undefined
                        : `/${paths.slice(0, index + 1).join('/')}`,
                  }))}
                  slots={
                    location.pathname.includes(enrollmentRoute) &&
                    location.pathname !== enrollmentRoute
                      ? [{ type: 'saveIndicator' }]
                      : [
                          {
                            type: 'tabs',
                            tabValue: allPaths[3] ?? 'fields',
                            defaultTabValue: 'fields',
                            tabs: [
                              {
                                label: t('fields'),
                                to: generatePath(GROWERS_ROUTES.DETAIL, {
                                  userId: !!userId ? `${userId}` : '',
                                }),
                                value: 'fields',
                              },
                              {
                                label: t('enrollments'),
                                to: enrollmentRoute,
                                value: 'enrollments',
                              },
                              // future nav item, but doesn't currently have a UI implemented
                              // {
                              //   label: t('documents'),
                              //   to: generatePath(GROWERS_ROUTES.ENROLLMENTS, {
                              //     userId: !!userId ? `${userId}` : '',
                              //   }),
                              //   value: 'documents',
                              // },
                            ],
                          },
                        ]
                  }
                />
              )}
              <Outlet />
            </Stack>
          </Container>
        </ProgramEngagementProvider>
      </SaveIndicatorProvider>
    </Can>
  )
}
